import React, { useState, useEffect, useRef } from 'react';
import HelpPanel from './HelpPanel.jsx';
import { RunBtn } from '../../components/ui';

// Simple DQN demo with a linear function approximator (one-hot state → Q values)
const DIRS = [[-1,0],[1,0],[0,-1],[0,1]];
function idx(x,y,w){return x + y*w}

export default function DeepRLLab() {
  const W = 5, H = 5;
  const start = [0,0];
  const goal = [4,4];
  const walls = useRef(new Set([idx(1,2,W), idx(2,2,W)]));

  // network: one hidden layer MLP (hidden x N) -> (4 x hidden)
  const HIDDEN = 32;
  const paramsRef = useRef(null);
  function initParams(){
    const N = W*H;
    const p = {
      W1: new Float32Array(HIDDEN * N),
      b1: new Float32Array(HIDDEN),
      W2: new Float32Array(4 * HIDDEN),
      b2: new Float32Array(4),
    };
    for(let i=0;i<p.W1.length;i++) p.W1[i] = (Math.random()-0.5) * 0.12;
    for(let i=0;i<p.W2.length;i++) p.W2[i] = (Math.random()-0.5) * 0.12;
    paramsRef.current = p;
  }
  if (!paramsRef.current) initParams();
  const bufferRef = useRef([]);

  const [agent, setAgent] = useState(start);
  const [epsilon, setEpsilon] = useState(0.15);
  const [lr, setLr] = useState(0.5);
  const [gamma, setGamma] = useState(0.95);
  const [batchSize, setBatchSize] = useState(16);
  const [bufferSize, setBufferSize] = useState(200);
  const [running, setRunning] = useState(false);
  const [episode, setEpisode] = useState(0);
  const [log, setLog] = useState([]);
  const [tick, setTick] = useState(0);
  const [maxSteps, setMaxSteps] = useState(200);
  const [returnsHist, setReturnsHist] = useState([]);
  const [lossHist, setLossHist] = useState([]);

  useEffect(() => { setAgent(start); }, []);

  function legal(x,y){ if (x<0||y<0||x>=W||y>=H) return false; if (walls.current.has(idx(x,y,W))) return false; return true }
  function stepState(pos, a){ const nx = pos[0]+DIRS[a][0], ny = pos[1]+DIRS[a][1]; if (!legal(nx,ny)) return pos; return [nx,ny]; }
  function isGoal(pos){ return pos[0]===goal[0] && pos[1]===goal[1]; }

  function predictQs(stateIndex){
    const p = paramsRef.current;
    const N = W*H;
    // hidden = tanh(W1[:, s] + b1)
    const hidden = new Float32Array(HIDDEN);
    for(let h=0; h<HIDDEN; h++){
      hidden[h] = Math.tanh(p.W1[h*N + stateIndex] + p.b1[h]);
    }
    const qs = new Float32Array(4);
    for(let a=0; a<4; a++){
      let sum = p.b2[a];
      const base = a*HIDDEN;
      for(let h=0; h<HIDDEN; h++) sum += p.W2[base + h] * hidden[h];
      qs[a] = sum;
    }
    return qs;
  }

  function chooseAction(stateIndex, eps){ if (Math.random() < eps) return Math.floor(Math.random()*4); const qs = predictQs(stateIndex); let bi=0; for(let a=1;a<4;a++) if (qs[a]>qs[bi]) bi=a; return bi }

  function pushTransition(t){ const buf=bufferRef.current; buf.push(t); if (buf.length>bufferSize) buf.shift(); }

  function sampleBatch(n){ const buf = bufferRef.current; const out = []; for(let i=0;i<n;i++){ out.push(buf[Math.floor(Math.random()*buf.length)]) } return out }

  function trainOnBatch(batch){
    const p = paramsRef.current; const N = W*H;
    const dW1 = new Float32Array(p.W1.length).fill(0);
    const db1 = new Float32Array(p.b1.length).fill(0);
    const dW2 = new Float32Array(p.W2.length).fill(0);
    const db2 = new Float32Array(p.b2.length).fill(0);
    let count = 0;

    for(const tr of batch){
      const {s,a,r,ns,done} = tr;
      const sidx = s; const nsidx = ns;
      // forward for s
      const hidden = new Float32Array(HIDDEN);
      for(let h=0; h<HIDDEN; h++) hidden[h] = Math.tanh(p.W1[h*N + sidx] + p.b1[h]);
      const pred = p.W2[a*HIDDEN + 0] * hidden[0]; // we'll compute properly below
      let qpred = p.b2[a];
      for(let h=0; h<HIDDEN; h++) qpred += p.W2[a*HIDDEN + h] * hidden[h];

      // forward for ns to compute maxNext
      let maxNext = -1e9;
      for(let aa=0; aa<4; aa++){
        // compute q(ns, aa)
        let qn = p.b2[aa];
        for(let h=0; h<HIDDEN; h++) qn += p.W2[aa*HIDDEN + h] * Math.tanh(p.W1[h*N + nsidx] + p.b1[h]);
        if (qn > maxNext) maxNext = qn;
      }
      const target = r + (done ? 0 : gamma * maxNext);
      const err = qpred - target; // (pred - target)

      // gradients: dW2[a,h] += err * hidden[h]
      for(let h=0; h<HIDDEN; h++){
        dW2[a*HIDDEN + h] += err * hidden[h];
      }
      db2[a] += err;

      // backprop to hidden: dh = err * W2[a,h] * (1 - hidden[h]^2)
      for(let h=0; h<HIDDEN; h++){
        const dh = err * p.W2[a*HIDDEN + h] * (1 - hidden[h]*hidden[h]);
        dW1[h*N + sidx] += dh; // input is one-hot -> only column sidx
        db1[h] += dh;
      }

      count++;
    }

    if (count===0) return;
    const scale = lr / count;
    for(let i=0;i<p.W1.length;i++) p.W1[i] -= scale * dW1[i];
    for(let i=0;i<p.b1.length;i++) p.b1[i] -= scale * db1[i];
    for(let i=0;i<p.W2.length;i++) p.W2[i] -= scale * dW2[i];
    for(let i=0;i<p.b2.length;i++) p.b2[i] -= scale * db2[i];
    setTick(t=>t+1);
    // compute MSE loss for this batch (average of err^2)
    let mse = 0;
    for(const tr of batch){
      const {s,a,r,ns,done} = tr;
      const sidx = s; const nsidx = ns;
      // current prediction
      const hidden = new Float32Array(HIDDEN);
      for(let h=0; h<HIDDEN; h++) hidden[h] = Math.tanh(p.W1[h*N + sidx] + p.b1[h]);
      let qpred = p.b2[a]; for(let h=0; h<HIDDEN; h++) qpred += p.W2[a*HIDDEN + h] * hidden[h];
      // target
      let maxNext = -1e9; for(let aa=0; aa<4; aa++){ let qn = p.b2[aa]; for(let h=0; h<HIDDEN; h++) qn += p.W2[aa*HIDDEN + h] * Math.tanh(p.W1[h*N + nsidx] + p.b1[h]); if (qn>maxNext) maxNext=qn }
      const target = r + (done ? 0 : gamma * maxNext);
      const err = qpred - target;
      mse += err*err;
    }
    mse = mse / batch.length;
    setLossHist(h => [mse].concat(h).slice(0,200));
  }

  async function runEpisodeTrain(){
    let pos = [...start]; let total=0;
    for(let t=0;t<maxSteps;t++){
      const s = idx(pos[0],pos[1],W);
      const a = chooseAction(s, epsilon);
      const npos = stepState(pos,a);
      const reward = isGoal(npos) ? 1 : -0.01;
      total += reward;
      const done = isGoal(npos);
      pushTransition({s, a, r: reward, ns: idx(npos[0],npos[1],W), done});
      if (bufferRef.current.length >= Math.max(4, batchSize)){
        const batch = sampleBatch(batchSize);
        trainOnBatch(batch);
      }
      pos = npos;
      setAgent([...pos]);
      if (done) break;
      await new Promise(r => setTimeout(r, 20));
      if (!running) break;
    }
    setEpisode(e => e+1);
    setLog(l => [total].concat(l).slice(0,200));
    setReturnsHist(rh => [total].concat(rh).slice(0,200));
    return total;
  }

  useEffect(() => {
    let cancelled = false;
    (async ()=>{
      while(running && !cancelled){ await runEpisodeTrain(); await new Promise(r=>setTimeout(r,80)); }
    })();
    return ()=>{ cancelled = true; };
  }, [running, epsilon, batchSize, bufferSize, lr, gamma, maxSteps]);

  function resetNetwork(){ initParams(); setTick(t=>t+1); }
  function resetBuffer(){ bufferRef.current = []; setTick(t=>t+1); }

  function renderCell(x,y){ const index = idx(x,y,W); const isWall = walls.current.has(index); const isStart = x===start[0] && y===start[1]; const isG = x===goal[0] && y===goal[1];
    const qs = predictQs(index); let bestA=0; for(let a=1;a<4;a++) if (qs[a]>qs[bestA]) bestA=a; const bestV = qs[bestA]; const policyChar = ['↑','↓','←','→'][bestA];
    return (
      <div key={index} style={{ border: '1px solid var(--b0)', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', background: isWall ? 'var(--b0)' : isG ? 'var(--gcyan)' : 'transparent' }}>
        {isStart && <div style={{ position: 'absolute', top: 4, left: 6, fontSize: 11, color: 'var(--t3)' }}>S</div>}
        {isG && <div style={{ position: 'absolute', top: 4, left: 6, fontSize: 11, color: 'var(--t3)' }}>G</div>}
        {agent[0]===x && agent[1]===y ? <div style={{ width: 28, height: 28, borderRadius: 4, background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--void)', fontWeight: 800 }}>{'•'}</div> : <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{policyChar}</div>
          <div style={{ fontSize: 11, color: 'var(--t3)' }}>{bestV.toFixed(2)}</div>
        </div>}
      </div>
    );
  }

  function Sparkline({data, color='#4ade80', width=220, height=48}){
    if (!data || data.length===0) return <div style={{ width, height, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--t3)' }}>—</div>;
    const vals = data.slice().reverse(); // newest first
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = Math.max(1e-6, max-min);
    const step = width / Math.max(1, vals.length-1);
    const points = vals.map((v,i)=> `${i*step},${height - ((v-min)/range) * height}` ).join(' ');
    return (
      <svg width={width} height={height} style={{ background: 'transparent' }}>
        <polyline fill="none" stroke={color} strokeWidth="2" points={points} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', gap: 18 }}>
        <div style={{ minWidth: 380 }}>
          <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 800 }}>DQN Demo (linear approximator)</div>
          <div style={{ marginTop: 6, color: 'var(--t2)' }}>Train a simple linear Q-network on the gridworld with experience replay.</div>
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(5,64px)', gap: 6 }}>
            {Array.from({length:H}).map((_,y)=> Array.from({length:W}).map((__,x)=> renderCell(x,y))).flat()}
          </div>
        </div>

        <div style={{ flex: 1, maxWidth: 560 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <RunBtn running={running} onRun={() => setRunning(true)} onStop={() => setRunning(false)} label="TRAIN" />
            <button onClick={() => runEpisodeTrain()} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>STEP EPISODE</button>
            <button onClick={() => { setAgent(start); }} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>RESET AGENT</button>
            <button onClick={resetNetwork} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>RESET NETWORK</button>
            <button onClick={resetBuffer} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>CLEAR BUFFER</button>
          </div>

          <div style={{ marginBottom: 12 }}>
            <HelpPanel title="DQN — Quick Tutorial" items={[
              ['Goal', 'Train an agent to reach the goal cell; reward = +1, step cost = -0.01.'],
              ['Train', 'Click Train to run episodes continuously. Use Step Episode to run one episode.'],
              ['Epsilon', 'Exploration rate — higher = more random actions. Anneal towards greedy.'],
              ['Replay Buffer', 'Stores transitions; batches are sampled to update the network.'],
              ['Network', 'Small MLP maps one-hot state → Q-values. Loss is MSE to TD target.'],
            ]} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label style={{ fontSize: 12 }}>Epsilon
              <input type="range" min="0" max="1" step="0.01" value={epsilon} onChange={e=>setEpsilon(parseFloat(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>LR
              <input type="range" min="0.01" max="1" step="0.01" value={lr} onChange={e=>setLr(parseFloat(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>Gamma
              <input type="range" min="0" max="1" step="0.01" value={gamma} onChange={e=>setGamma(parseFloat(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>Batch
              <input type="range" min="1" max="64" step="1" value={batchSize} onChange={e=>setBatchSize(parseInt(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>Buffer
              <input type="range" min="10" max="1000" step="10" value={bufferSize} onChange={e=>setBufferSize(parseInt(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>MaxSteps
              <input type="range" min="10" max="1000" step="10" value={maxSteps} onChange={e=>setMaxSteps(parseInt(e.target.value))} />
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--t3)' }}>Episode: <strong style={{ color: 'var(--t1)' }}>{episode}</strong></div>
            <div style={{ marginTop: 8, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Recent Episode Rewards</div>
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--b0)', padding: 8, borderRadius: 4 }}>
                {log.length === 0 ? <div style={{ color: 'var(--t3)' }}>No episodes yet.</div> : log.map((v,i)=>(<div key={i} style={{ fontSize: 13 }}>{(log.length-i)+'. '} {v.toFixed(3)}</div>))}
              </div>
            </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 6 }}>Recent Returns</div>
                  <div style={{ border: '1px solid var(--b0)', padding: 6, borderRadius: 4 }}><Sparkline data={returnsHist} color="#4ade80" /></div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 6 }}>Training Loss (MSE)</div>
                  <div style={{ border: '1px solid var(--b0)', padding: 6, borderRadius: 4 }}><Sparkline data={lossHist} color="#60a5fa" /></div>
                </div>
              </div>

              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--t3)' }}>
                Replay buffer: {bufferRef.current.length} · Tick: {tick}
              </div>
          </div>
        </div>
      </div>
    </div>
  );
}
